import {
  enableOptionalTracking,
  ensureGoogleTag,
  hasOptionalConsent,
  isLocalTrackingHost,
} from '@/lib/analyticsConsent';

const sentConversions = new Set();

const normaliseProductId = (value) => String(value || 'phantompath_service')
  .trim()
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 80) || 'phantompath_service';

const parseGbpValue = (value) => {
  const amount = Number.parseFloat(String(value || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(amount) && amount >= 0 ? Number(amount.toFixed(2)) : 0;
};

const localReferenceHash = (value) => {
  const input = String(value || 'verified_purchase');
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const createAnonymousConversionId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `pp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const reserveConversion = (verificationReference) => {
  const referenceHash = localReferenceHash(verificationReference);
  const storageKey = `phantompath_purchase_conversion_v1_${referenceHash}`;

  try {
    const existingId = window.sessionStorage.getItem(storageKey);
    if (existingId) return null;
    const conversionId = createAnonymousConversionId();
    window.sessionStorage.setItem(storageKey, conversionId);
    return conversionId;
  } catch (_error) {
    if (sentConversions.has(referenceHash)) return null;
    sentConversions.add(referenceHash);
    return createAnonymousConversionId();
  }
};

export const trackVerifiedPurchase = ({ product, duration, price, verificationReference }) => {
  if (typeof window === 'undefined' || isLocalTrackingHost()) return false;

  const conversionId = reserveConversion(verificationReference);
  if (!conversionId) return false;

  // GA4 remains governed by Google Consent Mode. When optional consent has not
  // been granted this sends a consent-denied measurement signal without
  // enabling analytics or advertising storage. Meta and TikTok remain fully
  // disabled unless the visitor explicitly accepted optional tracking.
  const optionalConsentGranted = hasOptionalConsent();
  ensureGoogleTag();
  if (optionalConsentGranted) enableOptionalTracking();

  const value = parseGbpValue(price);
  const productName = String(product || 'PhantomPath service').trim().slice(0, 120);
  const productId = normaliseProductId(productName);
  const serviceDuration = String(duration || '').trim().slice(0, 60);

  window.gtag?.('event', 'purchase', {
    transaction_id: conversionId,
    value,
    currency: 'GBP',
    items: [{
      item_id: productId,
      item_name: productName,
      item_category: 'phantompath_service',
      price: value,
      quantity: 1,
    }],
    service_duration: serviceDuration,
  });

  if (optionalConsentGranted) {
    // Meta Purchase is sent by the backend Conversions API after payment
    // confirmation. Do not send a second browser Purchase with an unrelated
    // event ID, otherwise one order can be counted as two conversions.
    window.ttq?.track?.('Purchase', {
      event_id: conversionId,
      value,
      currency: 'GBP',
      content_id: productId,
      content_name: productName,
      content_type: 'product',
      quantity: 1,
      price: value,
      contents: [{ content_id: productId, content_name: productName, quantity: 1, price: value }],
    });
  }

  return true;
};
